import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { View, Text, Platform } from "react-native";

import { t } from '../../../i18n/translations';
import { useLanguageStore } from '../../quiz-core/languageStore';

const IQES = {
  panel: "#0B1026",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  green: "#7FE08E",
  amber: "#F8C84E",
  red: "#EF6B63",
};

interface PlayerResultsCardProps {
  score: number;
  totalQuestions: number;
  accuracy: number;
}

const PlayerResultsCard: React.FC<PlayerResultsCardProps> = ({ score, totalQuestions, accuracy }) => {
  const { language } = useLanguageStore();
  const getPerformanceColor = () => {
    if (accuracy >= 80) return IQES.green;
    if (accuracy >= 60) return IQES.amber;
    return IQES.red;
  };

  const getPerformanceIcon = () => {
    if (accuracy >= 80) return 'trophy-outline';
    if (accuracy >= 60) return 'ribbon-outline';
    return 'trending-up-outline';
  };

  const getPerformanceMessage = () => {
    if (accuracy >= 80) return t('results.excellentPerformance', language);
    if (accuracy >= 60) return t('results.goodJob', language);
    return t('results.keepPracticing', language);
  };

  return (
    <View
      style={{
        backgroundColor: IQES.panel,
        borderRadius: 24,
        borderWidth: 1,
        borderColor: IQES.border,
        paddingVertical: 32,
        paddingHorizontal: 32,
        alignItems: 'center',
        alignSelf: 'center',
        maxWidth: 360,
        marginHorizontal: 20,
        ...Platform.select({
          ios: {
            shadowColor: IQES.gold,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.18,
            shadowRadius: 12,
          },
          android: {
            elevation: 4,
          },
          web: {
            boxShadow: '0 4px 16px rgba(248, 200, 78, 0.18)',
          },
        }),
      }}
    >
      {/* Performance Icon */}
      <View style={{
        backgroundColor: "rgba(248, 200, 78, 0.14)",
        borderRadius: 999,
        padding: 16,
        marginBottom: 20,
        borderWidth: 1,
        borderColor: IQES.border,
        ...Platform.select({
          ios: {
            shadowColor: getPerformanceColor(),
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
          },
          android: {
            elevation: 6,
          },
          web: {
            boxShadow: `0 4px 16px ${getPerformanceColor()}40`,
          },
        }),
      }}>
        <Ionicons 
          name={getPerformanceIcon()} 
          size={32} 
          color="#FFFFFF" 
        />
      </View>

      {/* Performance Message */}
      <Text
        style={{
          fontSize: 18,
          color: getPerformanceColor(),
          fontWeight: '700',
          marginBottom: 24,
          textAlign: 'center',
          letterSpacing: 0.5
        }}
      >
        {getPerformanceMessage()}
      </Text>

      {/* Score Section */}
      <View style={{
        backgroundColor: IQES.panelLift,
        borderRadius: 16,
        paddingVertical: 20,
        paddingHorizontal: 24,
        marginBottom: 20,
        width: '100%',
        alignItems: 'center'
      }}>
        <Text
          style={{
            fontSize: 16,
            color: IQES.muted,
            fontWeight: '600',
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          {t('results.finalScore', language)}
        </Text>
        <Text
          style={{
            fontSize: 48,
            color: IQES.ivory,
            fontWeight: '800',
            marginBottom: 4,
            lineHeight: 52
          }}
        >
          {score}
        </Text>
        <Text
          style={{
            fontSize: 18,
            color: IQES.muted,
            fontWeight: '500'
          }}
        >
          {t('results.outOf', language)} {totalQuestions}
        </Text>
      </View>

      {/* Accuracy Section */}
      <View style={{
        backgroundColor: "rgba(248, 200, 78, 0.12)",
        borderWidth: 1,
        borderColor: "rgba(248, 200, 78, 0.25)",
        borderRadius: 16,
        paddingVertical: 16,
        paddingHorizontal: 24,
        marginBottom: 24,
        width: '100%',
        alignItems: 'center'
      }}>
        <Text
          style={{
            fontSize: 16,
            color: IQES.muted,
            fontWeight: '600',
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          {t('results.accuracyRate', language)}
        </Text>
        <Text
          style={{
            fontSize: 36,
            color: getPerformanceColor(),
            fontWeight: '800',
            lineHeight: 40
          }}
        >
          {accuracy}%
        </Text>
      </View>

      {/* Success Message */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(127, 224, 142, 0.13)',
        borderWidth: 1,
        borderColor: "rgba(127, 224, 142, 0.28)",
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 16,
        width: '100%'
      }}>
        <Ionicons 
          name="checkmark-circle" 
          size={20} 
          color="#10b981" 
          style={{ marginRight: 8 }} 
        />
        <Text style={{
          fontSize: 14,
          color: IQES.green,
          fontWeight: '600',
          flex: 1,
          textAlign: 'center'
        }}>
          {t('results.resultsSavedSuccessfully', language)}
        </Text>
      </View>
    </View>
  );
};

export default PlayerResultsCard;
